import { createComponent, createHook } from "reakit-system";
import type { ComponentPropsWithoutRef } from "react";
import { useContext } from "react";
import { cx, css } from "@emotion/css";
import type { SystemStyleObject } from "@styled-system/css";
import type { BoxOptions } from "../../Box/Box";
import { useBox } from "../../Box/Box";
import { searchContext } from "./context";
import { WHCM } from "../../constants";
import type { StylesOptions } from "../../hooks";

type Options = BoxOptions;
type Props = Omit<ComponentPropsWithoutRef<"div">, "role">;

const activeRowStyles: SystemStyleObject = {
  gap: "sp-12",
  "&[data-highlighted=true], &:hover": {
    backgroundColor: "gray-10",
    borderLeftColor: "gray-50",
  },
  '> [role="gridcell"]': {
    minHeight: "2rem",
    py: "sp-4",
    '&[class*="OptionAction"], &[class*="LinkCell"]': {
      minHeight: "auto",
      py: "sp-0",
    },
  },
  [WHCM]: {
    backgroundColor: "Window",
    borderLeftColor: "Window",
    color: "WindowText",
    'a, button, [class*=Link], [class*=Hint], [class*="Link"]:hover, a:visited [class*="Link"]': {
      color: "WindowText",
    },
    "&[data-highlighted=true], &:hover": {
      backgroundColor: "Highlight",
      borderLeftColor: "Highlight",
      'a, button, [class*=Link], [class*=Hint], [class*="Link"]:hover, a:visited [class*="Link"]': {
        color: "HighlightText",
      },
    },
  },
};

const useRowStyles = createHook<StylesOptions, ComponentPropsWithoutRef<"div">>({
  useOptions(options) {
    return {
      cursor: "pointer",
      p: "sp-8",
      pr: ["sp-16", "sp-48"],
      pl: "sp-12",
      bg: "white",
      display: "flex",
      alignItems: "flex-start",
      flexWrap: "nowrap",
      borderLeft: 4,
      borderLeftColor: "transparent",
      ...options,
      styles: {
        ...options.styles,
        ...activeRowStyles,
      },
    };
  },
  useProps(_, props) {
    const { activeRow } = useContext(searchContext);
    const active = activeRow?.id === props.id;
    return { ...props, "data-highlighted": active.toString() };
  },
});

const useRow = createHook<Options, Props>({
  compose: [useBox, useRowStyles],
  useProps(_, { className, ...props }) {
    return {
      ...props,
      role: "row",
      className: cx(className, css({ label: "SearchFieldRow" })),
      onMouseDownCapture: (event) => event.preventDefault(),
    };
  },
});

export const Row = createComponent<"div", Options>({
  as: "div",
  useHook: useRow,
});
